if (!customElements.get('theme-product-reviews')) {
  class ThemeProductReviews extends HTMLElement {
    connectedCallback() {
      this.initializeReviewModal();
      this.initializeReviewPagination();

      const realReviews = this.getRealReviewData();
      const rating = this.dataset.ratingMode === 'real'
        ? realReviews.rating
        : this.dataset.ratingMode === 'random'
        ? this.getPersistentRandomRating()
        : this.normalizeRating(this.dataset.fixedRating);
      const count = this.dataset.countMode === 'real'
        ? realReviews.count
        : this.dataset.countMode === 'fixed'
        ? Math.max(0, Math.trunc(Number(this.dataset.fixedCount) || 0))
        : this.getPersistentRandomCount();
      const numberFormatter = new Intl.NumberFormat(this.dataset.languageLocale || undefined);

      this.hidden = this.dataset.hideWithoutReviews === 'true' && realReviews.count === 0;

      this.dataset.rating = rating.toFixed(1);
      this.dataset.count = String(count);

      this.querySelectorAll('[data-global-rating]').forEach((element) => {
        element.textContent = rating.toFixed(1);
      });

      this.querySelectorAll('[data-global-count]').forEach((element) => {
        element.textContent = numberFormatter.format(count);
      });

      const globalStars = this.querySelector('[data-global-review-stars]');
      if (globalStars) {
        const visualRating = Math.floor(rating * 2) / 2;
        globalStars.style.setProperty('--theme-review-stars-fill', `${this.getFillPercentage(visualRating, globalStars)}%`);
        globalStars.setAttribute('aria-label', this.getRatingLabel(rating));
      }

      this.updateDistribution(rating, count, numberFormatter);
      this.updateRemainingReviews(count, numberFormatter);

      document.dispatchEvent(new CustomEvent('theme:product-reviews-updated', {
        detail: { productId: String(this.dataset.productId || '0') }
      }));

      if (this.querySelector('[data-review-feedback]')) this.openReviewModal(false);
    }

    disconnectedCallback() {
      // Remove o listener exclusivo da paginação quando a seção sai do DOM.
      if (this.reviewPaginationInitialized) {
        this.reviewPagination.removeEventListener('click', this.handleReviewPaginationClick);
        this.reviewDesktopMediaQuery.removeEventListener('change', this.handleReviewViewportChange);
        this.reviewPaginationInitialized = false;
      }

      if (!this.reviewModalInitialized) return;

      this.reviewModalOpenButton?.removeEventListener('click', this.handleReviewModalOpen);
      this.reviewModalCloseButtons.forEach((button) => {
        button.removeEventListener('click', this.handleReviewModalClose);
      });
      document.removeEventListener('keydown', this.handleReviewModalKeydown);
      document.body.classList.remove('theme-reviews-modal-open');
      this.reviewModalInitialized = false;
    }

    // Inicializa a paginação quando os cards ultrapassam o limite de alguma viewport.
    initializeReviewPagination() {
      this.reviewPagination = this.querySelector('[data-review-pagination]');

      // Se a navegação não foi renderizada, os cards cabem em uma página nos dois limites.
      if (!this.reviewPagination) return;

      this.reviewList = this.querySelector('[id^="product-reviews-list-"]');
      this.reviewCards = Array.from(this.querySelectorAll('[data-review-card]'));
      this.reviewPageSizeDesktop = Math.max(1, Number(this.dataset.reviewsPerPageDesktop) || 10);
      this.reviewPageSizeMobile = Math.max(1, Number(this.dataset.reviewsPerPageMobile) || 4);
      this.reviewDesktopMediaQuery = window.matchMedia('(min-width: 750px)');
      this.reviewPreviousButton = this.reviewPagination.querySelector('[data-review-page="previous"]');
      this.reviewNextButton = this.reviewPagination.querySelector('[data-review-page="next"]');
      this.reviewNumberButtons = Array.from(this.reviewPagination.querySelectorAll('[data-review-page-number]'));
      this.currentReviewPage = 1;

      // Recalcula a quantidade visível quando a viewport cruza o breakpoint do tema.
      this.handleReviewViewportChange = () => this.updateReviewPaginationLayout();

      // Converte cada clique da navegação no número da página solicitada.
      this.handleReviewPaginationClick = (event) => {
        const button = event.target.closest('[data-review-page]');

        // Ignora cliques fora dos botões ou em controles desativados.
        if (!button || button.disabled) return;

        const requestedPage = button.dataset.reviewPage === 'previous'
          ? this.currentReviewPage - 1
          : button.dataset.reviewPage === 'next'
          ? this.currentReviewPage + 1
          : Number(button.dataset.reviewPage);

        this.showReviewPage(requestedPage, true);
      };

      this.reviewPagination.addEventListener('click', this.handleReviewPaginationClick);
      this.reviewDesktopMediaQuery.addEventListener('change', this.handleReviewViewportChange);
      this.reviewPaginationInitialized = true;
      this.updateReviewPaginationLayout();
    }

    // Aplica o limite configurado para a viewport e recalcula o total de páginas.
    updateReviewPaginationLayout() {
      this.reviewPageSize = this.reviewDesktopMediaQuery.matches
        ? this.reviewPageSizeDesktop
        : this.reviewPageSizeMobile;
      this.reviewPageCount = Math.max(1, Math.ceil(this.reviewCards.length / this.reviewPageSize));

      // Oculta a navegação quando todos os cards cabem na viewport atual.
      this.reviewPagination.hidden = this.reviewPageCount === 1;

      this.showReviewPage(this.currentReviewPage, false);
    }

    // Exibe somente os cards da página ativa conforme o limite responsivo configurado.
    showReviewPage(requestedPage, scrollToReviews) {
      const normalizedPage = Number.isFinite(requestedPage) ? requestedPage : 1;
      const page = Math.min(this.reviewPageCount, Math.max(1, normalizedPage));
      const firstVisibleIndex = (page - 1) * this.reviewPageSize;
      const lastVisibleIndex = firstVisibleIndex + this.reviewPageSize;
      const visiblePageButtonCount = this.reviewDesktopMediaQuery.matches ? 7 : 3;
      const firstVisiblePageButton = Math.max(
        1,
        Math.min(page - Math.floor(visiblePageButtonCount / 2), this.reviewPageCount - visiblePageButtonCount + 1)
      );
      const lastVisiblePageButton = Math.min(
        this.reviewPageCount,
        firstVisiblePageButton + visiblePageButtonCount - 1
      );

      this.reviewCards.forEach((card, index) => {
        card.hidden = index < firstVisibleIndex || index >= lastVisibleIndex;
      });

      this.reviewNumberButtons.forEach((button) => {
        const buttonPage = Number(button.dataset.reviewPage);
        const isCurrentPage = buttonPage === page;

        // Limita os números visíveis para a paginação não ultrapassar a largura da viewport.
        button.hidden = buttonPage < firstVisiblePageButton || buttonPage > lastVisiblePageButton;
        button.toggleAttribute('aria-current', isCurrentPage);

        // O valor semântico correto do atributo ativo é "page".
        if (isCurrentPage) button.setAttribute('aria-current', 'page');
      });

      this.reviewPreviousButton.disabled = page === 1;
      this.reviewNextButton.disabled = page === this.reviewPageCount;
      this.currentReviewPage = page;

      // Após uma troca solicitada pela cliente, reposiciona a tela no início dos reviews.
      if (scrollToReviews) this.reviewList.scrollIntoView({ block: 'start' });
    }

    initializeReviewModal() {
      if (this.reviewModalInitialized) return;

      this.reviewModal = this.querySelector('[data-review-modal]');
      this.reviewModalOpenButton = this.querySelector('[data-review-modal-open]');
      this.reviewModalCloseButtons = Array.from(this.querySelectorAll('[data-review-modal-close]'));
      if (!this.reviewModal || !this.reviewModalOpenButton) return;

      this.handleReviewModalOpen = () => this.openReviewModal(true);
      this.handleReviewModalClose = () => this.closeReviewModal();
      this.handleReviewModalKeydown = (event) => {
        if (this.reviewModal.hidden) return;

        if (event.key === 'Escape') {
          event.preventDefault();
          this.closeReviewModal();
          return;
        }

        if (event.key !== 'Tab') return;

        const focusableElements = this.getReviewModalFocusableElements();
        if (focusableElements.length === 0) return;

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (event.shiftKey && document.activeElement === firstElement) {
          event.preventDefault();
          lastElement.focus();
        }
        else if (!event.shiftKey && document.activeElement === lastElement) {
          event.preventDefault();
          firstElement.focus();
        }
      };

      this.reviewModalOpenButton.addEventListener('click', this.handleReviewModalOpen);
      this.reviewModalCloseButtons.forEach((button) => {
        button.addEventListener('click', this.handleReviewModalClose);
      });
      document.addEventListener('keydown', this.handleReviewModalKeydown);
      this.reviewModalInitialized = true;
    }

    openReviewModal(rememberFocus = true) {
      if (!this.reviewModal) return;

      if (rememberFocus) this.reviewModalPreviousFocus = document.activeElement;
      this.reviewModal.hidden = false;
      document.body.classList.add('theme-reviews-modal-open');

      requestAnimationFrame(() => {
        const feedback = this.reviewModal.querySelector('[data-review-feedback]');
        const focusTarget = feedback || this.getReviewModalFocusableElements()[0];
        focusTarget?.focus();
      });
    }

    closeReviewModal() {
      if (!this.reviewModal) return;

      this.reviewModal.hidden = true;
      document.body.classList.remove('theme-reviews-modal-open');
      if (this.reviewModalPreviousFocus instanceof HTMLElement) this.reviewModalPreviousFocus.focus();
    }

    getReviewModalFocusableElements() {
      if (!this.reviewModal) return [];

      return Array.from(this.reviewModal.querySelectorAll(
        'button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), a[href]'
      )).filter((element) => element.offsetParent !== null && element.getAttribute('tabindex') !== '-1');
    }

    getRealReviewData() {
      const productId = String(this.dataset.productId || '0');
      const reviewSections = Array.from(document.querySelectorAll('theme-product-reviews'))
        .filter((section) => String(section.dataset.productId || '0') === productId);
      const count = reviewSections.reduce(
        (total, section) => total + Math.max(0, Math.trunc(Number(section.dataset.blockCount) || 0)),
        0
      );
      const ratingTotal = reviewSections.reduce(
        (total, section) => total + Math.max(0, Number(section.dataset.realRatingTotal) || 0),
        0
      );

      return {
        count,
        rating: count > 0 ? this.normalizeRating(ratingTotal / count) : 0
      };
    }

    normalizeRating(value) {
      const rating = Number(value);
      return Math.min(5, Math.max(0, Number.isFinite(rating) ? rating : 0));
    }

    getPersistentRandomRating() {
      const configuredMin = Math.round(this.normalizeRating(this.dataset.ratingRandomMin) * 10);
      const configuredMax = Math.round(this.normalizeRating(this.dataset.ratingRandomMax) * 10);
      const min = Math.min(configuredMin, configuredMax);
      const max = Math.max(configuredMin, configuredMax);
      const rangeSize = max - min + 1;
      const productId = String(this.dataset.productId || '0');
      const storageKey = `mavella:review-ratings:v1:${min}:${max}`;

      try {
        const assignments = this.readAssignments(storageKey);
        const storedRating = Number(assignments[productId]);

        if (Number.isInteger(storedRating) && storedRating >= min && storedRating <= max) {
          return storedRating / 10;
        }

        const initialRating = Math.round(this.normalizeRating(this.dataset.rating) * 10);
        const rating = initialRating >= min && initialRating <= max
          ? initialRating
          : min + (this.hashProductId(productId) % rangeSize);

        assignments[productId] = rating;
        localStorage.setItem(storageKey, JSON.stringify(assignments));
        return rating / 10;
      }
      catch (error) {
        return (min + (this.hashProductId(productId) % rangeSize)) / 10;
      }
    }

    getPersistentRandomCount() {
      const configuredMin = Math.trunc(Number(this.dataset.randomMin) || 0);
      const configuredMax = Math.trunc(Number(this.dataset.randomMax) || configuredMin);
      const min = Math.max(0, Math.min(configuredMin, configuredMax));
      const max = Math.max(min, configuredMin, configuredMax);
      const rangeSize = max - min + 1;
      const productId = String(this.dataset.productId || '0');
      const storageKey = `mavella:review-counts:v1:${min}:${max}`;

      try {
        const assignments = this.readAssignments(storageKey);
        const usedByOtherProducts = new Set(
          Object.entries(assignments)
            .filter(([assignedProductId]) => assignedProductId !== productId)
            .map(([, value]) => Number(value))
            .filter(Number.isInteger)
        );
        const storedCount = Number(assignments[productId]);

        if (Number.isInteger(storedCount) && storedCount >= min && !usedByOtherProducts.has(storedCount)) {
          return storedCount;
        }

        let count = min + (this.hashProductId(productId) % rangeSize);
        let attempts = 0;

        while (usedByOtherProducts.has(count) && attempts < rangeSize) {
          count = min + ((count - min + 1) % rangeSize);
          attempts += 1;
        }

        if (usedByOtherProducts.has(count)) {
          count = max + 1;
          while (usedByOtherProducts.has(count)) count += 1;
        }

        assignments[productId] = count;
        localStorage.setItem(storageKey, JSON.stringify(assignments));
        return count;
      }
      catch (error) {
        return min + (this.hashProductId(productId) % rangeSize);
      }
    }

    updateDistribution(rating, count, numberFormatter) {
      const percentages = this.getDistributionPercentages(rating);
      const distribution = percentages.map((percentage) => Math.floor(count * percentage / 100));
      const allocatedCount = distribution.reduce((total, rowCount) => total + rowCount, 0);
      const dominantIndex = percentages.indexOf(Math.max(...percentages));

      distribution[dominantIndex] += Math.max(0, count - allocatedCount);

      const largestCount = Math.max(1, ...distribution);

      this.querySelectorAll('[data-distribution-row]').forEach((row) => {
        const stars = Number(row.dataset.stars);
        const rowCount = distribution[stars - 1] || 0;
        const bar = row.querySelector('[data-distribution-bar]');
        const countElement = row.querySelector('[data-distribution-count]');

        if (bar) bar.style.setProperty('--reviews-distribution-width', `${(rowCount / largestCount) * 100}%`);
        if (countElement) countElement.textContent = numberFormatter.format(rowCount);
      });
    }

    getDistributionPercentages(rating) {
      if (this.dataset.ratingMode === 'random') {
        const weights = [1, 2, 3, 4, 5].map((stars) => {
          const weight = Number(this.dataset[`randomDistribution-${stars}`]);
          return Math.max(0, Number.isFinite(weight) ? weight : 0);
        });
        const totalWeight = weights.reduce((total, weight) => total + weight, 0);

        return totalWeight > 0
          ? weights.map((weight) => weight * 100 / totalWeight)
          : [0, 0, 0, 0, 100];
      }

      if (rating >= 4.8) return [1, 1, 2, 6, 90];
      if (rating >= 4.3) return [1, 2, 6, 45, 46];
      if (rating >= 3.8) return [1, 4, 20, 60, 15];
      if (rating >= 3.3) return [2, 12, 45, 32, 9];
      if (rating >= 2.8) return [6, 30, 45, 15, 4];
      if (rating >= 2.3) return [15, 50, 25, 8, 2];
      if (rating >= 1.8) return [40, 45, 10, 4, 1];
      return [90, 6, 2, 1, 1];
    }

    updateRemainingReviews(count, numberFormatter) {
      const renderedReviews = Math.max(0, Math.trunc(Number(this.dataset.blockCount) || 0));
      const remaining = Math.max(0, count - renderedReviews);
      const remainingElement = this.querySelector('[data-remaining-reviews]');
      const remainingNumber = this.querySelector('[data-remaining-reviews-number]');

      if (remainingElement) remainingElement.hidden = remaining === 0;
      if (remainingNumber) remainingNumber.textContent = numberFormatter.format(remaining);
    }

    getFillPercentage(rating, element) {
      const fullStars = Math.floor(rating);
      const hasHalfStar = rating - fullStars >= 0.5;
      const isCommon = element.classList.contains('theme-review-stars--common');
      const starWidth = isCommon ? 18 : 96;
      const gap = isCommon ? 4 : 8;
      const totalWidth = isCommon ? 106 : 512;
      let activeExtent = fullStars > 0
        ? (fullStars * starWidth) + ((fullStars - 1) * gap)
        : 0;

      if (hasHalfStar && fullStars < 5) {
        activeExtent += (fullStars > 0 ? gap : 0) + (starWidth / 2);
      }

      return (activeExtent / totalWidth) * 100;
    }

    getRatingLabel(rating) {
      const template = this.dataset.ratingLabel || '[rating] / 5';
      return template.replace('[rating]', rating.toFixed(1));
    }

    readAssignments(storageKey) {
      const storedValue = localStorage.getItem(storageKey);
      if (!storedValue) return {};

      const parsedValue = JSON.parse(storedValue);
      return parsedValue && typeof parsedValue === 'object' && !Array.isArray(parsedValue)
        ? parsedValue
        : {};
    }

    hashProductId(productId) {
      let hash = 2166136261;

      for (let index = 0; index < productId.length; index += 1) {
        hash ^= productId.charCodeAt(index);
        hash = Math.imul(hash, 16777619);
      }

      return hash >>> 0;
    }
  }

  customElements.define('theme-product-reviews', ThemeProductReviews);
}
